/**
 * A verdade sobre o pagamento.
 *
 * **Não existe cobrança neste produto ainda.** Não há gateway: `assinar` não
 * tira dinheiro de ninguém, não abre fatura e não libera plano pago. O que o
 * botão faz é REGISTRAR A INTENÇÃO no log de auditoria (quem, qual plano, qual
 * ciclo, o preço vigente na hora do pedido) e devolver `PAGAMENTO_INDISPONIVEL`.
 *
 * Por isso aqui não tem "processando pagamento", não tem barra de progresso e
 * não tem "assinatura confirmada". Uma tela que finge cobrar é pior do que uma
 * tela honesta: ela vira chamado de suporte ("paguei e não liberou"), e vira
 * cliente com a sensação de ter sido enganado em cima de algo que ninguém
 * cobrou. Enquanto não houver gateway, a frase é esta.
 *
 * O aviso é um bloco fixo na tela, e não um toast: toast some em 4 segundos e a
 * informação mais importante da tela não pode depender de quem estava olhando.
 */
import { Info } from 'lucide-react';
import { cn } from '@mgdlead/ui';

export const PAGAMENTO_AINDA_NAO_DISPONIVEL =
  'O pagamento ainda não está disponível dentro do sistema — nada foi cobrado de você.';

export interface AvisoDePagamentoProps {
  /** Nome do plano pedido, quando o aviso responde a um pedido já feito. */
  plano?: string | null;
  /** Ciclo pedido, em português ("Anual"). */
  ciclo?: string | null;
  className?: string;
}

/**
 * O aviso ANTES de qualquer clique: explica o que o botão vai (e não vai) fazer.
 *
 * ⚠️ A frase **muda com o catálogo**, não com uma constante. O dono preenche os
 * links da Kiwify aos poucos; um texto fixo dizendo "pagamento indisponível"
 * continuaria no ar no dia seguinte ao primeiro link entrar, e ninguém
 * reclamaria — a tela pareceria certa. Por isso `checkoutDisponivel` vem de
 * quem leu o catálogo.
 */
export function AvisoDePagamento({
  checkoutDisponivel = false,
  className,
}: {
  checkoutDisponivel?: boolean;
  className?: string;
}) {
  return (
    <aside
      className={cn(
        'flex items-start gap-2 rounded-lg border border-info/30 bg-info/10 p-3 text-sm',
        className,
      )}
    >
      <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-info" />
      <p className="min-w-0 text-foreground">
        {checkoutDisponivel ? (
          <>
            O pagamento acontece numa página externa e segura. Depois de pagar,{' '}
            <strong className="font-medium">use o mesmo e-mail da sua conta aqui</strong> — é por
            ele que a liberação do plano é feita.
          </>
        ) : (
          <>
            {PAGAMENTO_AINDA_NAO_DISPONIVEL} Ao escolher um plano você registra o pedido, e a nossa
            equipe entra em contato para combinar a forma de pagamento.
          </>
        )}
      </p>
    </aside>
  );
}

/** O aviso DEPOIS do pedido registrado. Confirma o pedido, não uma cobrança. */
export function PedidoRegistrado({ plano, ciclo, className }: AvisoDePagamentoProps) {
  return (
    <aside
      role="status"
      className={cn(
        'flex flex-col gap-1 rounded-lg border border-success/30 bg-success/10 p-3 text-sm',
        className,
      )}
    >
      <p className="font-medium text-foreground">
        Pedido registrado
        {plano ? (
          <>
            {' · '}
            {plano}
            {ciclo ? ` · ${ciclo}` : ''}
          </>
        ) : null}
      </p>
      <p className="text-muted-foreground">
        {PAGAMENTO_AINDA_NAO_DISPONIVEL} Seu plano continua o mesmo por enquanto. Alguém da nossa
        equipe vai entrar em contato para combinar o pagamento e liberar a troca.
      </p>
    </aside>
  );
}
